import { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { api } from '../services/api';
import type { AdminTwinDetail, TwinMatch, TwinMatchInterview, TwinMatchingInsight } from '../types';
import { Layout } from '../components/layout/Layout';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { TwinProfileDetails } from '../components/twins/TwinProfileDetails';
import { formatDate, formatDateTime } from '../utils/helpers';
import {
  ArrowLeft,
  Bot,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Users,
  ExternalLink,
  TrendingUp,
  Trophy,
  Heart,
  ChevronsUpDown,
  ChevronsDownUp,
  RotateCcw,
  Loader2,
  AlertCircle,
  CalendarCheck,
} from 'lucide-react';

const INTERVIEW_STATUS: Record<
  TwinMatchInterview['status'],
  { label: string; variant: 'success' | 'error' | 'warning' | 'info' | 'default' }
> = {
  not_sent: { label: 'Not sent', variant: 'default' },
  pending: { label: 'Awaiting reply', variant: 'info' },
  accepted: { label: 'Accepted', variant: 'success' },
  declined: { label: 'Declined', variant: 'error' },
  expired: { label: 'Expired - no reply', variant: 'warning' },
};

function InterviewDetails({ matches, onNavigate }: {
  matches: TwinMatch[];
  onNavigate: (userId: string) => void;
}) {
  const rows = [...matches].sort((a, b) => {
    // Sent requests first, newest first; unsent after.
    const ta = a.interview?.sentAt ? new Date(a.interview.sentAt).getTime() : 0;
    const tb = b.interview?.sentAt ? new Date(b.interview.sentAt).getTime() : 0;
    return tb - ta;
  });
  const sent = rows.filter(m => m.interview && m.interview.status !== 'not_sent').length;
  const accepted = rows.filter(m => m.interview?.status === 'accepted').length;

  return (
    <div className="rounded-xl border border-[#252535] bg-[#141420] p-5 space-y-4">
      <div className="flex items-start gap-3">
        <CalendarCheck className="w-5 h-5 text-[#3b82f6] shrink-0 mt-0.5" />
        <div>
          <h2 className="text-sm font-semibold text-[#f1f5f9]">Interview details</h2>
          <p className="text-xs text-[#64748b] mt-1">
            Interview requests sent on this twin&apos;s matches: {sent} of {rows.length} sent, {accepted} accepted.
            Candidates have 24 hours to reply.
          </p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#64748b]">No matches yet, so no interview requests.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-[#252535] text-[#64748b] text-xs uppercase tracking-wide">
                <th className="py-2 pr-3">Matched with</th>
                <th className="py-2 pr-3">Match %</th>
                <th className="py-2 pr-3">Request sent</th>
                <th className="py-2 pr-3">Sent at</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Replied at</th>
              </tr>
            </thead>
            <tbody className="text-[#cbd5e1]">
              {rows.map(m => {
                const iv = m.interview;
                const status = iv ? INTERVIEW_STATUS[iv.status] : null;
                return (
                  <tr key={m.matchId} className="border-b border-[#1f1f2e] align-top">
                    <td className="py-2.5 pr-3">
                      <span className="text-[#f1f5f9] font-medium">{m.matchedTwin.name}</span>
                      {m.matchedUser.id ? (
                        <button
                          type="button"
                          onClick={() => onNavigate(m.matchedUser.id)}
                          className="block text-xs text-[#3b82f6] hover:underline text-left"
                        >
                          {m.matchedUser.name}
                        </button>
                      ) : null}
                    </td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">
                      <MatchPercentageBadge pct={m.matchPercentage} />
                    </td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">
                      {iv ? (iv.status === 'not_sent' ? 'No' : 'Yes') : '-'}
                    </td>
                    <td className="py-2.5 pr-3 whitespace-nowrap text-xs text-[#94a3b8]">
                      {iv?.sentAt ? formatDateTime(iv.sentAt) : '-'}
                    </td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">
                      {status ? (
                        <Badge variant={status.variant} className="text-[10px]">{status.label}</Badge>
                      ) : '-'}
                      {iv?.status === 'pending' && iv.responseDeadline ? (
                        <p className="text-[10px] text-[#64748b] mt-1">
                          Reply by {formatDateTime(iv.responseDeadline)}
                        </p>
                      ) : null}
                    </td>
                    <td className="py-2.5 whitespace-nowrap text-xs text-[#94a3b8]">
                      {iv?.respondedAt ? formatDateTime(iv.respondedAt) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatInsightStage(stage: string): string {
  const labels: Record<string, string> = {
    prefilter_blocked: 'Prefilter blocked (legacy)',
    screening_rejected: 'LLM screening rejected (legacy)',
    screening_passed_no_chat_outcome: 'LLM passed (legacy, chat pending)',
    shortlist_skipped: 'Passed screening, not shortlisted (legacy)',
    shortlisted_for_twin_chat: 'Shortlisted, chat pending',
    twin_chat_matched: 'Twin chat - matched',
    twin_chat_no_match: 'Twin chat - no match',
    twin_chat_needs_followup: 'Twin chat - needs follow-up',
    twin_chat_inactive: 'Twin chat - inactive',
    twin_chat_reused: 'Twin chat - reused transcript',
  };
  return labels[stage] ?? stage.replace(/_/g, ' ');
}

/**
 * Stages the pipeline can no longer produce. Matching now runs on exact
 * domain + job_title equality with no prefilter or LLM screening step - these
 * stages only appear on twin_match_cache rows written before that change.
 */
const LEGACY_STAGES = new Set([
  'prefilter_blocked',
  'screening_rejected',
  'screening_passed_no_chat_outcome',
  'shortlist_skipped',
]);

/**
 * Frontend-composed summary of the last matching run, reflecting the current
 * exact domain + job_title match pipeline (no prefilter/LLM screening step).
 * Written client-side instead of showing the backend's own `message` string,
 * which still uses legacy "prefilter blocked"/"LLM screened" wording left over
 * from the old heuristic pipeline (those counts are always 0 / == candidatePool
 * now, since nothing is rejected pre-chat anymore).
 */
function describeMatchingRun(run: NonNullable<AdminTwinDetail['matchingLastRun']>): string {
  const pool = run.candidatePool ?? 0;
  const shortlisted = run.shortlistedForChat ?? pool;
  const chats = run.twinChatsProcessed ?? 0;
  const matched = run.twinChatsMatched;

  if (pool === 0) {
    return 'No candidates matched this twin\'s domain + job title exactly on this run.';
  }

  const cappedNote =
    run.candidatePoolCapped && run.candidatePoolBeforeCap
      ? ` (capped from ${run.candidatePoolBeforeCap})`
      : '';

  const parts = [`${pool} candidate(s) matched by domain + job title${cappedNote}`];
  parts.push(`${shortlisted} shortlisted for twin chat`);
  if (chats > 0) {
    parts.push(`${chats} chat(s) completed${matched != null ? `, ${matched} met match criteria` : ''}`);
  } else if (shortlisted > 0) {
    parts.push('chats not yet run');
  }
  return `${parts.join(', ')}.`;
}

function decisionBadgeVariant(decision: string): 'success' | 'error' | 'default' {
  if (decision === 'accepted') return 'success';
  if (decision === 'rejected') return 'error';
  return 'default';
}

function MatchPercentageBadge({ pct }: { pct: number }) {
  const variant = pct >= 80 ? 'success' : pct >= 60 ? 'info' : 'warning';
  return <Badge variant={variant}>{pct}%</Badge>;
}

function StatCard({ label, value, icon: Icon, color }: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-[#1c1c2a] border border-[#252535] rounded-xl p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0`} style={{ background: `${color}18` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-[#64748b] uppercase tracking-wider">{label}</p>
        <p className="text-xl font-semibold text-[#f1f5f9]">{value}</p>
      </div>
    </div>
  );
}

function MatchCard({ match, expanded, onToggle, onNavigate }: {
  match: TwinMatch;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: (userId: string) => void;
}) {
  const pct = match.matchPercentage;
  const barColor = pct >= 80 ? '#22c55e' : pct >= 60 ? '#3b82f6' : '#f59e0b';

  return (
    <div className="border border-[#252535] rounded-xl overflow-hidden">
      <button
        className="w-full text-left p-4 hover:bg-[#1c1c2a] transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <Bot className="w-3.5 h-3.5 text-[#3b82f6] shrink-0" />
              <span className="text-sm font-medium text-[#f1f5f9] truncate">{match.thisTwin.name}</span>
              <span className="text-xs text-[#64748b] truncate">({match.thisUser.name})</span>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-[#64748b] shrink-0" />
            <div className="flex items-center gap-1.5 min-w-0">
              <Bot className="w-3.5 h-3.5 text-[#a855f7] shrink-0" />
              <span className="text-sm font-medium text-[#f1f5f9] truncate">{match.matchedTwin.name}</span>
              <span className="text-xs text-[#64748b] truncate">({match.matchedUser.name})</span>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <MatchPercentageBadge pct={pct} />
            <span className="text-xs text-[#64748b] hidden sm:block">{formatDate(match.createdAt)}</span>
            {expanded
              ? <ChevronUp className="w-4 h-4 text-[#64748b]" />
              : <ChevronDown className="w-4 h-4 text-[#64748b]" />}
          </div>
        </div>
        <div className="mt-3 h-1 bg-[#252535] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: barColor }}
          />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-[#252535] pt-3 space-y-3 bg-[#0f0f1a]">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#1c1c2a] rounded-xl p-3">
              <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">This Twin's Owner</p>
              <button
                onClick={(e) => { e.stopPropagation(); onNavigate(match.thisUser.id); }}
                className="flex items-center gap-1 text-sm font-medium text-[#f1f5f9] hover:text-[#3b82f6] transition-colors"
              >
                <Users className="w-3.5 h-3.5 shrink-0" />
                {match.thisUser.name}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </button>
            </div>
            <div className="bg-[#1c1c2a] rounded-xl p-3">
              <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Matched User</p>
              <button
                onClick={(e) => { e.stopPropagation(); onNavigate(match.matchedUser.id); }}
                className="flex items-center gap-1 text-sm font-medium text-[#f1f5f9] hover:text-[#a855f7] transition-colors"
              >
                <Users className="w-3.5 h-3.5 shrink-0" />
                {match.matchedUser.name}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </button>
            </div>
          </div>

          {match.summary ? (
            <div>
              <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2">AI Match Summary</p>
              <div className="bg-[#141420] border border-[#252535] rounded-xl p-3">
                <p className="text-sm text-[#94a3b8] leading-relaxed whitespace-pre-line">{match.summary}</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-[#64748b] italic">No summary available</p>
          )}

          <p className="text-xs text-[#64748b]">Matched {formatDate(match.createdAt)}</p>
        </div>
      )}
    </div>
  );
}

type SortKey = 'pct_desc' | 'pct_asc' | 'date_desc' | 'date_asc';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'pct_desc', label: 'Match % - High to Low' },
  { value: 'pct_asc',  label: 'Match % - Low to High' },
  { value: 'date_desc', label: 'Date - Newest First' },
  { value: 'date_asc',  label: 'Date - Oldest First' },
];

function formatTwinCreatedAt(value: AdminTwinDetail['createdAt']): string {
  if (value == null) return '-';
  if (typeof value === 'string') return formatDate(value);
  if (typeof value === 'object' && 'seconds' in (value as object)) {
    const sec = Number((value as { seconds: number }).seconds);
    if (!Number.isFinite(sec)) return '-';
    return formatDate(new Date(sec * 1000));
  }
  return '-';
}

export function TwinDetail() {
  const { twinId } = useParams<{ twinId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Opened from a user's page -> back returns there; otherwise to the twins list.
  const backTo = (location.state as { from?: string } | null)?.from ?? '/twins';
  const { toast } = useToast();
  const [matches, setMatches] = useState<TwinMatch[]>([]);
  const [matchingInsights, setMatchingInsights] = useState<TwinMatchingInsight[]>([]);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [twinDetail, setTwinDetail] = useState<AdminTwinDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('pct_desc');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isResetting, setIsResetting] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetAcknowledged, setResetAcknowledged] = useState(false);

  const loadTwinPage = useCallback(
    async (options?: { showErrors?: boolean; withPageSpinner?: boolean }) => {
      if (!twinId) return;
      const showErrors = options?.showErrors !== false;
      const withPageSpinner = options?.withPageSpinner !== false;
      if (withPageSpinner) setLoading(true);
      try {
        const results = await Promise.allSettled([
          api.getTwinMatches(twinId),
          api.getTwinDetail(twinId),
          api.getTwinMatchingInsights(twinId),
        ]);
        const [mRes, dRes, iRes] = results;
        if (mRes.status === 'fulfilled') setMatches(mRes.value.matches);
        else {
          console.error(mRes.reason);
          if (showErrors) {
            toast(
              mRes.reason instanceof Error ? mRes.reason.message : 'Failed to load twin matches.',
              'error',
            );
          }
          setMatches([]);
        }
        if (dRes.status === 'fulfilled') setTwinDetail(dRes.value.twin);
        else {
          console.error(dRes.reason);
          if (showErrors) {
            toast(
              dRes.reason instanceof Error ? dRes.reason.message : 'Failed to load twin profile.',
              'error',
            );
          }
          setTwinDetail(null);
        }
        if (iRes.status === 'fulfilled') setMatchingInsights(iRes.value.insights);
        else {
          console.error(iRes.reason);
          setMatchingInsights([]);
          if (showErrors) {
            toast(
              iRes.reason instanceof Error
                ? iRes.reason.message
                : 'Failed to load matching decisions for this twin.',
              'error',
            );
          }
        }
      } finally {
        if (withPageSpinner) setLoading(false);
      }
    },
    [twinId, toast],
  );

  useEffect(() => {
    void loadTwinPage({ showErrors: true, withPageSpinner: true });
  }, [loadTwinPage]);

  const displayName = twinDetail?.name?.trim() || matches[0]?.thisTwin.name || twinId || 'Twin';

  const sorted = useMemo(() => {
    return [...matches].sort((a, b) => {
      switch (sortKey) {
        case 'pct_desc':  return b.matchPercentage - a.matchPercentage;
        case 'pct_asc':   return a.matchPercentage - b.matchPercentage;
        case 'date_desc': return (b.createdAt > a.createdAt ? 1 : -1);
        case 'date_asc':  return (a.createdAt > b.createdAt ? 1 : -1);
      }
    });
  }, [matches, sortKey]);

  const stats = useMemo(() => {
    if (!matches.length) return null;
    const pcts = matches.map(m => m.matchPercentage);
    const avg = Math.round(pcts.reduce((s, p) => s + p, 0) / pcts.length);
    const highest = Math.max(...pcts);
    return { total: matches.length, avg, highest };
  }, [matches]);

  const allExpanded = sorted.length > 0 && expandedIds.size === sorted.length;

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(sorted.map(m => m.matchId)));
    }
  };

  const toggleOne = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleNavigateUser = (userId: string) => navigate(`/users/${userId}`);

  const openResetModal = () => {
    setResetAcknowledged(false);
    setResetModalOpen(true);
  };

  const closeResetModal = () => {
    if (isResetting) return;
    setResetModalOpen(false);
    setResetAcknowledged(false);
  };

  const executeResetMatching = async () => {
    if (!twinId || isResetting || !resetAcknowledged) return;
    setIsResetting(true);
    try {
      const r = await api.resetTwinMatching(twinId);
      toast(
        `Matching reset: ${r.deletedTwinChats} twin chats, ${r.deletedUserChats} user threads, ${r.deletedCacheEntries} cache entries removed.`,
        'success',
      );
      setExpandedIds(new Set());
      setResetModalOpen(false);
      setResetAcknowledged(false);
      await loadTwinPage({ showErrors: false, withPageSpinner: false });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to reset twin matching.', 'error');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Layout>
      <Modal
        isOpen={resetModalOpen}
        onClose={closeResetModal}
        title="Reset matching data?"
        size="md"
        closeOnBackdropClick={!isResetting}
        allowDismiss={!isResetting}
        footer={(
          <>
            <button
              type="button"
              onClick={closeResetModal}
              disabled={isResetting}
              className="rounded-lg border border-[#2a2a3a] bg-[#141420] px-4 py-2 text-sm font-medium text-[#e2e8f0] hover:bg-[#1c1c2a] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void executeResetMatching()}
              disabled={!resetAcknowledged || isResetting}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isResetting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isResetting ? 'Resetting…' : 'Reset matching data'}
            </button>
          </>
        )}
      >
        <p className="text-sm text-[#94a3b8] mb-3">
          Twin: <span className="font-medium text-[#f1f5f9]">{displayName}</span>
          {twinId ? (
            <span className="block mt-1 font-mono text-xs text-[#64748b] break-all">{twinId}</span>
          ) : null}
        </p>
        <p className="text-sm text-[#cbd5e1] mb-3">This permanently removes:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#94a3b8] mb-4">
          <li>AI twin-to-twin conversations (<code className="text-xs text-[#64748b]">twins_chat</code>) for pairs involving this twin</li>
          <li>User match threads in <code className="text-xs text-[#64748b]">chats</code> (both users lose that thread)</li>
          <li>Screening / chat result cache for those pairs</li>
          <li>The twin&apos;s stored &quot;best match&quot; score (set to 0)</li>
        </ul>
        <div className="rounded-lg border border-[#334155] bg-[#0f172a]/50 p-3 mb-4">
          <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide mb-1">Be aware</p>
          <ul className="list-disc pl-4 space-y-1 text-xs text-[#64748b] leading-relaxed">
            <li>There is no transaction across collections: if something fails partway through, you may need to run reset again.</li>
            <li>If matching is running for this twin at the same time, you could race with the worker-prefer doing this while matching is off.</li>
            <li>Users must start matching again from the product; this does not start the worker by itself.</li>
          </ul>
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#2a2a3a] bg-[#141420] p-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#475569] bg-[#1e293b] text-amber-600 focus:ring-amber-500"
            checked={resetAcknowledged}
            disabled={isResetting}
            onChange={(e) => setResetAcknowledged(e.target.checked)}
          />
          <span className="text-sm text-[#e2e8f0]">
            I understand this cannot be undone and affects both users in each deleted match thread.
          </span>
        </label>
      </Modal>

      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(backTo)}
            className="p-2 rounded-xl bg-[#1c1c2a] hover:bg-[#252535] transition-colors text-[#94a3b8] hover:text-[#f1f5f9]"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-[#f1f5f9]">
              {displayName} - Matches
            </h1>
            <p className="text-sm text-[#64748b]">ID: {twinId}</p>
          </div>
        </div>

        {!loading && twinDetail && (
          <div className="rounded-xl border border-[#252535] bg-[#141420] p-5 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="info" className="capitalize">
                {twinDetail.type === 'unknown' ? 'Type unknown' : twinDetail.type.replace('_', ' ')}
              </Badge>
              {twinDetail.active && !twinDetail.locked ? (
                <Badge variant="success">Active</Badge>
              ) : null}
              {!twinDetail.active && !twinDetail.locked ? (
                <Badge variant="default">Inactive</Badge>
              ) : null}
              {twinDetail.locked ? <Badge variant="warning">Locked</Badge> : null}
              {twinDetail.running ? (
                <Badge variant="info">Matching running</Badge>
              ) : null}
            </div>

            {twinDetail.matchingLastRun ? (
              <div className="rounded-lg border border-[#334155] bg-[#0f172a]/60 px-4 py-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">
                  Last matching run
                  {twinDetail.matchingLastRun.completedAt
                    ? ` · ${formatDate(twinDetail.matchingLastRun.completedAt)}`
                    : ''}
                </p>
                <p className="text-sm text-[#cbd5e1] leading-relaxed">
                  {describeMatchingRun(twinDetail.matchingLastRun)}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[#64748b] font-mono">
                  {twinDetail.matchingLastRun.candidatePool != null ? (
                    <span>pool={twinDetail.matchingLastRun.candidatePool}</span>
                  ) : null}
                  {twinDetail.matchingLastRun.candidatePoolCapped ? (
                    <span>capped_from={twinDetail.matchingLastRun.candidatePoolBeforeCap}</span>
                  ) : null}
                  {twinDetail.matchingLastRun.shortlistedForChat != null ? (
                    <span>shortlisted={twinDetail.matchingLastRun.shortlistedForChat}</span>
                  ) : null}
                  {twinDetail.matchingLastRun.twinChatsProcessed != null ? (
                    <span>twin_chats={twinDetail.matchingLastRun.twinChatsProcessed}</span>
                  ) : null}
                </div>
                <p className="text-[10px] text-[#64748b] italic">
                  Matching runs on an exact domain + job title match today - no prefilter or LLM screening step
                  is performed anymore, so every matched candidate goes straight to shortlist.
                </p>
              </div>
            ) : twinDetail.running ? (
              <p className="text-sm text-[#94a3b8]">
                Matching is marked as running. If this stays for a long time, the worker may still be processing or the last run has not recorded a summary yet.
              </p>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1">Owner</p>
                {twinDetail.owner.id ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/users/${twinDetail.owner.id}`)}
                    className="text-left text-sm font-medium text-[#f1f5f9] hover:text-[#3b82f6] flex items-center gap-1"
                  >
                    {twinDetail.owner.name}
                    <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
                  </button>
                ) : (
                  <p className="text-sm text-[#94a3b8]">-</p>
                )}
                {twinDetail.owner.email ? (
                  <p className="text-xs text-[#64748b] mt-0.5 truncate">{twinDetail.owner.email}</p>
                ) : null}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1">Understanding</p>
                <p className="text-lg font-semibold text-[#f1f5f9]">{Math.round(Number(twinDetail.understanding) || 0)}%</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1">Best match</p>
                <p className="text-lg font-semibold text-[#f1f5f9]">
                  {(twinDetail.highestMatch ?? 0) > 0 ? `${Math.round(twinDetail.highestMatch)}%` : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1">Twin chats</p>
                <p className="text-lg font-semibold text-[#f1f5f9]">{twinDetail.matchCount}</p>
              </div>
            </div>

            {twinDetail.purpose ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1">Purpose</p>
                <p className="text-sm text-[#94a3b8] leading-relaxed whitespace-pre-wrap">{twinDetail.purpose}</p>
              </div>
            ) : null}

            {(twinDetail.role || twinDetail.target) ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {twinDetail.role ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1">Role</p>
                    <p className="text-sm text-[#94a3b8]">{twinDetail.role}</p>
                  </div>
                ) : null}
                {twinDetail.target ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-1">Target</p>
                    <p className="text-sm text-[#94a3b8]">{twinDetail.target}</p>
                  </div>
                ) : null}
              </div>
            ) : null}

            {twinDetail.profileDetails ? (
              <div className="border-t border-[#252535] pt-4">
                <TwinProfileDetails type={twinDetail.type} details={twinDetail.profileDetails} />
              </div>
            ) : null}

            <p className="text-xs text-[#64748b]">Created {formatTwinCreatedAt(twinDetail.createdAt)}</p>

            <div className="border-t border-[#252535] pt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-[#94a3b8] max-w-xl">
                Clear twin AI conversations, user match threads, and <code className="text-[#64748b]">twin_match_cache</code> records so the matching worker can run fresh (start matching again from the product).
              </p>
              <button
                type="button"
                onClick={openResetModal}
                disabled={isResetting || loading}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isResetting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                Reset matching data
              </button>
            </div>
          </div>
        )}

        {!loading && !twinDetail && twinId ? (
          <div className="rounded-xl border border-[#252535] bg-[#141420] p-5">
            <p className="text-sm text-[#94a3b8] mb-3">Twin profile could not be loaded. You can still reset matching if this twin exists in the directory.</p>
            <button
              type="button"
              onClick={openResetModal}
              disabled={isResetting}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isResetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Reset matching data
            </button>
          </div>
        ) : null}

        {!loading && twinId ? (
          <InterviewDetails matches={matches} onNavigate={handleNavigateUser} />
        ) : null}

        {!loading && twinId ? (
          <div className="rounded-xl border border-[#252535] bg-[#141420] p-5 space-y-4">
            <button
              type="button"
              onClick={() => setInsightsOpen(open => !open)}
              aria-expanded={insightsOpen}
              className="w-full flex items-center gap-3 text-left"
            >
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
              <h2 className="flex-1 text-sm font-semibold text-[#f1f5f9]">
                Matching decisions (per pair)
                <span className="ml-2 text-xs font-normal text-[#64748b]">
                  {matchingInsights.length} pair{matchingInsights.length !== 1 ? 's' : ''}
                </span>
              </h2>
              <span className="flex items-center gap-1 text-xs text-[#64748b]">
                {insightsOpen ? 'Hide' : 'Show'}
                {insightsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </button>
            {insightsOpen ? (
            <>
            <div className="flex items-start gap-3">
              <div className="w-5 shrink-0" />
              <div>
                <p className="text-xs text-[#64748b] mt-1 leading-relaxed">
                  Each row is a Firestore <code className="text-[#94a3b8]">twin_match_cache</code> record: why a candidate was blocked, rejected, accepted for screening, skipped for twin chat, or how the twin-to-twin chat ended.
                  User-visible match threads are listed below.
                </p>
                <p className="text-xs text-[#64748b] mt-1 leading-relaxed italic">
                  Matching now uses exact domain + job title matching only - stages marked "(legacy)" are from
                  records created before that change and won&apos;t appear on new runs.
                </p>
              </div>
            </div>
            {matchingInsights.length === 0 ? (
              <p className="text-sm text-[#64748b]">
                No per-pair decisions in <code className="text-[#94a3b8]">twin_match_cache</code> yet.
                {twinDetail?.matchingLastRun?.message
                  ? ' See the last matching run summary above for what happened.'
                  : ' Start matching from the product; if the candidate pool is empty (e.g. company twin with no active job seekers), nothing will be stored per pair.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm min-w-[720px]">
                  <thead>
                    <tr className="border-b border-[#252535] text-[#64748b] text-xs uppercase tracking-wide">
                      <th className="py-2 pr-3">Other twin</th>
                      <th className="py-2 pr-3">Owner</th>
                      <th className="py-2 pr-3">Decision</th>
                      <th className="py-2 pr-3">Stage</th>
                      <th className="py-2 pr-3">Match %</th>
                      <th className="py-2 pr-3">Why</th>
                      <th className="py-2">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#cbd5e1]">
                    {matchingInsights.map(row => (
                      <tr
                        key={row.cacheDocId}
                        className={`border-b border-[#1f1f2e] align-top ${
                          LEGACY_STAGES.has(row.outcomeStage) ? 'opacity-60' : ''
                        }`}
                      >
                        <td className="py-2.5 pr-3">
                          <span className="text-[#f1f5f9] font-medium">{row.otherTwin.name || row.otherTwinId}</span>
                          <p className="text-[10px] text-[#64748b] font-mono truncate max-w-[220px]">{row.otherTwinId}</p>
                        </td>
                        <td className="py-2.5 pr-3">
                          {row.otherUser.id ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/users/${row.otherUser.id}`)}
                              className="text-[#3b82f6] hover:underline text-left"
                            >
                              {row.otherUser.name}
                            </button>
                          ) : (
                            <span className="text-[#64748b]">-</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 whitespace-nowrap">
                          <div className="flex flex-col gap-1 items-start">
                            <Badge variant={decisionBadgeVariant(row.decision)} className="text-[10px] capitalize">
                              {row.decision || 'unknown'}
                            </Badge>
                            {row.hidden ? (
                              <span title="Swept aside by a review-limit restart. Not deleted - clearing the recruiter twin's hidden set brings it back.">
                                <Badge variant="warning" className="text-[10px]">
                                  Hidden from recruiter
                                </Badge>
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="py-2.5 pr-3">
                          <span className="text-xs text-[#94a3b8] leading-snug">
                            {formatInsightStage(row.outcomeStage)}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 whitespace-nowrap">
                          {row.matchPercentage != null ? (
                            <MatchPercentageBadge pct={Math.round(row.matchPercentage)} />
                          ) : (
                            <span className="text-[#64748b] text-xs">-</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-[#94a3b8] max-w-md">
                          <p className="leading-relaxed whitespace-pre-wrap break-words">{row.reason}</p>
                          {row.screenScore != null ? (
                            <p className="text-[10px] text-[#64748b] mt-1">
                              Screen score: {Math.round(row.screenScore)}
                            </p>
                          ) : null}
                          {row.prefilterScore != null && row.outcomeStage === 'prefilter_blocked' ? (
                            <p className="text-[10px] text-[#64748b] mt-1">
                              Prefilter score: {Math.round(row.prefilterScore)}
                            </p>
                          ) : null}
                          {row.prefilterBlockers && row.prefilterBlockers.length > 0 ? (
                            <p className="text-[10px] text-[#64748b] mt-1">
                              Blockers: {row.prefilterBlockers.join(', ')}
                            </p>
                          ) : null}
                        </td>
                        <td className="py-2.5 text-[#64748b] text-xs whitespace-nowrap">
                          {row.updatedAt ? formatDate(row.updatedAt) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
          </div>
        ) : matches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Bot className="w-12 h-12 text-[#64748b] mb-3" />
            <p className="text-[#94a3b8]">No matches yet for this twin</p>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Total Matches" value={stats!.total} icon={Heart} color="#f472b6" />
              <StatCard label="Average Match" value={`${stats!.avg}%`} icon={TrendingUp} color="#3b82f6" />
              <StatCard label="Highest Match" value={`${stats!.highest}%`} icon={Trophy} color="#22c55e" />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-[#64748b]">
                {matches.length} match{matches.length !== 1 ? 'es' : ''}
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={sortKey}
                  onChange={e => setSortKey(e.target.value as SortKey)}
                  className="text-sm bg-[#1c1c2a] border border-[#252535] text-[#f1f5f9] rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#3b82f6]"
                >
                  {SORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  onClick={toggleAll}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-[#1c1c2a] border border-[#252535] text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#3b82f6] transition-colors"
                >
                  {allExpanded
                    ? <><ChevronsDownUp className="w-3.5 h-3.5" /> Collapse all</>
                    : <><ChevronsUpDown className="w-3.5 h-3.5" /> Expand all</>}
                </button>
              </div>
            </div>

            {/* Match list */}
            <div className="space-y-3">
              {sorted.map(match => (
                <MatchCard
                  key={match.matchId}
                  match={match}
                  expanded={expandedIds.has(match.matchId)}
                  onToggle={() => toggleOne(match.matchId)}
                  onNavigate={handleNavigateUser}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
